<template>
  <UniversalPopup ref="universalPopupRef" />
  <div :class="[{ 'desktop-edit-page': isDesktop, 'mobile-edit-page': !isDesktop }]">
    <span class="edit-page-title">
      {{ $t("editor_home_title") }}
      <span class="edit-page-title-sub">
        {{ $t("editor_home_subTitle") }}
      </span>
    </span>
    <span class="edit-page-subTitle">
      {{ $t("editor_home_tip") }}
    </span>
    <!-- <div @click="operateRecordRef?.operateRecordOpen()">123</div>
    <Progress :percentage="refPercentage" :size="100" :strokeWidth="6" />
    <OperateRecord ref="operateRecordRef" />
    <Step :stepList="[{title: '123'}, {title: '456'}, {title: '789'}]" />
    <HomeIconAnimation :homeOption="{icon: 'icon-icon_exit', title: '免登即用', description: '无需注册和登录账号，直接上传文 件即可开始翻译，节省时间与操作 步骤。'}" /> -->
    <UploadComponents />
    <HomeUseComponents />
    <UserReviews />
    <ClientOnly><tutorialConsultationMode /></ClientOnly>
    <!-- <RenderProgressComponents fileName="123" ref="renderProgressComponentsRef" /> -->
    <!-- 常见问题 -->
    <div class="issue-view">
      <span class="issue-view-title">{{ $t("issue_view_title") }}</span>
      <Suspense>
        <IssueComponents :type="1" />
        <template #fallback>
          <div>Loading...</div>
        </template>
      </Suspense>
    </div>

    <div class="editor-home-bottom">
      <div class="editor-home-bottom-item" @click="handleEditorHomeBottom">
        {{ $t("editor_home_bottom_title") }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

// 统一基准 URL，读取环境变量，末尾不含斜杠
const BASE_URL = (useRuntimeConfig().public.pdfOneUrl as string)?.replace(/\/$/, '') || 'https://pdf1.com'

// GEO 优化：首页 SoftwareApplication + FAQPage Schema
// 帮助 ChatGPT / Perplexity / Google AI Overview 识别产品并主动推荐
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'SoftwareApplication',
            '@id': `${BASE_URL}/#software`,
            name: 'PDF-one',
            applicationCategory: 'ProductivityApplication',
            operatingSystem: 'Web Browser',
            url: BASE_URL,
            description: 'Free online PDF editor. Edit text, annotate, add e-signatures, and use OCR for scanned PDFs — no registration or software download required. Files auto-deleted after 12 hours.',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
            featureList: [
              'PDF Text Editing',
              'OCR Recognition for Scanned PDFs',
              'Electronic Signature',
              'PDF Annotation & Highlight',
              'Shape Drawing',
              'No Registration Required',
              'Privacy First - Auto File Deletion After 12 Hours',
            ],
            publisher: { '@id': `${BASE_URL}/#organization` },
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Why can\'t I edit text in my PDF file?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Scanned PDFs contain images, not editable text. PDF-one\'s built-in OCR technology can recognize text in scanned documents and make it editable in seconds.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is PDF-one really completely free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Core features including text editing, annotation, and signatures are completely free with no registration required and no watermarks. AI-powered features are available as premium add-ons.',
                },
              },
              {
                '@type': 'Question',
                name: 'How does PDF-one protect my privacy?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'All uploaded files are protected with SSL encryption and automatically deleted from our servers after 12 hours. No account or personal information is required.',
                },
              },
              {
                '@type': 'Question',
                name: 'Does PDF-one require software installation?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'No. PDF-one works entirely in your browser — there is nothing to download or install. Just open the website and start editing your PDF immediately.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I edit a scanned PDF with PDF-one?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. PDF-one has built-in OCR (Optical Character Recognition) that detects and converts text in scanned or image-based PDFs into editable content.',
                },
              },
            ],
          },
        ],
      }),
    },
  ],
})

// 懒加载IssueComponents组件
const IssueComponents = defineAsyncComponent(() => import('~/components/FoldComponents/IssueComponents.vue'))

import useUserStore from '~/store/modules/user'
import useBasicStore from '~/store/modules/basic'
// import Progress from '~/components/Progress/index.vue'
import UniversalPopup from '~/components/Dialog/UniversalPopup/index.vue'
// import VersionComponent from '~/components/Version/index.vue'
// import OperateRecord from '~/components/OperateRecord/index.vue'
// import Step from '~/components/Step/index.vue'
// import HomeIconAnimation from '~/components/HomeComponents/HomeIconAnimation/index.vue'
import UploadComponents from '~/components/UploadComponents/index.vue'
import HomeUseComponents from '~/components/HomeComponents/HomeUse/index.vue'
import UserReviews from '~/components/UserReviews/userReviews.vue'
import tutorialConsultationMode from '~/components/indexCom/tutorialConsultationMode.vue'
import RenderProgressComponents from '~/components/RenderProgressComponents/index.vue'
import eventBus from 'vue3-eventbus'
import { EventEditorHomeBottom } from '~/config/event'
// import useProStore from '~/store/modules/pro'
const viewport = useViewport();
const isDesktop = computed(() => viewport.breakpoint?.value.includes('desktop'))
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'


const userStore = useUserStore()
const basicStore = useBasicStore()
const {reviews} = storeToRefs(userStore)
const {languages, isChina, localtionData} = storeToRefs(basicStore)
const {t, locale } = useI18n()
const universalPopupRef = ref<InstanceType<typeof UniversalPopup>>()
const renderProgressComponentsRef = ref<InstanceType<typeof RenderProgressComponents>>()


const handleEditorHomeBottom = () => {
  eventBus.emit(EventEditorHomeBottom.editorHomeBottom)
}

onMounted(()=>{
  // 第二个窗口
  window.addEventListener('message', event => {
    // console.log("购买成功", event.data)
    const receivedMessage = event.data; // 从事件中获取接收到的消息
    if (receivedMessage && receivedMessage.refresh === true) {
      userStore.getUserInfo()
    }
  });
  
  // console.log(universalPopupRef.value, 'universalPopupRef')
  // universalPopupRef.value?.open({
  //   title: 'title',
  //   subTitle: 'subTitle',
  //   btn1: 'btn1',
  //   btn2: 'btn2'
  // })
  renderProgressComponentsRef.value?.open()
})

onUnmounted(()=>{
  window.removeEventListener('message', event => {});
})

</script>

<style lang="scss" scoped>
@use './index.scss' as *;
@use './mobile.scss' as *;
</style>
